import { useEffect, useRef, useState } from "react";
import { X, Upload, Save, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";

/**
 * Edit an owned block: brand name, destination URL, hover slogan and logo.
 * Reuses the same hi-res "contain" composite the claim panel uses so the
 * new logo stays crisp when it lands on the grid.
 */
export default function EditBlockModal({ block, open, onClose, onSaved }) {
  const [brand, setBrand] = useState("");
  const [url, setUrl] = useState("");
  const [slogan, setSlogan] = useState("");
  const [image, setImage] = useState("");
  const [rawImage, setRawImage] = useState("");
  const [bgColor, setBgColor] = useState("#ffffff");
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open || !block) return;
    setBrand(block.brand_name || "");
    setUrl(block.destination_url || "");
    setSlogan(block.hover_slogan || "");
    setImage(block.image_url || "");
    setRawImage("");
    setBgColor("#ffffff");
    setError("");
  }, [open, block]);

  const isSmallSelection = block && block.width * block.height < 100;

  const readFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { setError("Only images allowed"); return; }
    if (file.size > 800 * 1024) { setError("Image must be ≤ 800 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setRawImage(reader.result);
    reader.readAsDataURL(file);
  };

  // Same hi-res composite as ClaimPanel: contain-fit, 1200px long edge, padded bg.
  useEffect(() => {
    if (!rawImage || !block) return;
    const img = new Image();
    img.onload = () => {
      const aspect = block.width / block.height;
      const LONG = 2000;
      const targetW = aspect >= 1 ? LONG : Math.round(LONG * aspect);
      const targetH = aspect >= 1 ? Math.round(LONG / aspect) : LONG;
      const canvas = document.createElement("canvas");
      canvas.width = targetW; canvas.height = targetH;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, targetW, targetH);
      const pad = 0.86;
      const scale = Math.min((targetW * pad) / img.width, (targetH * pad) / img.height);
      const w = img.width * scale, h = img.height * scale;
      const x = (targetW - w) / 2, y = (targetH - h) / 2;
      ctx.drawImage(img, x, y, w, h);
      setImage(canvas.toDataURL("image/jpeg", 0.94));
    };
    img.onerror = () => setError("Could not read image");
    img.src = rawImage;
  }, [rawImage, bgColor, block]);

  const validate = () => {
    if (!brand.trim()) return "Brand name is required";
    if (!url.trim()) return "Destination URL is required";
    try { new URL(url); } catch { return "Enter a valid URL (include https://)"; }
    if (slogan.length > 70) return "Slogan must be 70 characters or less";
    return "";
  };

  const submit = async () => {
    const v = validate();
    if (v) { setError(v); return; }
    setBusy(true); setError("");
    try {
      await http.put(`/pixels/${block.id}`, {
        brand_name: brand.trim(),
        destination_url: url.trim(),
        hover_slogan: slogan.trim(),
        image_url: image,
      });
      toast.success("Block updated. Live on the grid.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open || !block) return null;

  return (
    <>
      <div className="slide-backdrop open" onClick={onClose} data-testid="edit-block-backdrop" />
      <aside className="slide-panel open" data-testid="edit-block-panel">
        <div className="p-6 border-b border-[var(--line-strong)]">
          <div className="flex items-start justify-between">
            <div>
              <div className="pill">Edit block</div>
              <h2 className="mt-2 font-display text-3xl leading-none">Update <span className="text-gradient">your ad</span></h2>
            </div>
            <button data-testid="close-edit-panel" onClick={onClose} className="p-2 rounded-full hover:bg-white/5"><X className="w-4 h-4" /></button>
          </div>
          <div className="mt-4 text-sm text-[var(--fg-muted)] font-mono">
            Block: ({block.x}, {block.y}) · {block.width}×{block.height} units · {block.width * 10}×{block.height * 10}px
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Company / Brand name</label>
            <input data-testid="edit-input-brand" value={brand} onChange={(e) => setBrand(e.target.value)} className="input-lux mt-1" maxLength={80} />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Target destination URL</label>
            <input data-testid="edit-input-url" type="url" value={url} onChange={(e) => setUrl(e.target.value)} className="input-lux mt-1" placeholder="https://your-site.com" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Hover slogan</label>
              <span className="text-xs font-mono text-[var(--fg-muted)]">{slogan.length}/70</span>
            </div>
            <input data-testid="edit-input-slogan" value={slogan} onChange={(e) => setSlogan(e.target.value.slice(0, 70))} className="input-lux mt-1" maxLength={70} />
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest text-[var(--fg-muted)]">Ad image</label>
            {isSmallSelection && (
              <div className="mt-1 flex items-start gap-2 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Small block ({block.width}×{block.height}) — use a square icon or simple wordmark for best clarity.</span>
              </div>
            )}
            <div
              data-testid="edit-drop-zone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); readFile(e.dataTransfer.files?.[0]); }}
              onClick={() => fileRef.current?.click()}
              className={`drop-zone mt-1 cursor-pointer ${dragOver ? "drag-over" : ""}`}
            >
              {image ? (
                <div className="flex items-center gap-3">
                  <img src={image} alt="preview" className="w-16 h-16 object-contain rounded-lg border border-[var(--line-strong)]" style={{ background: bgColor }} />
                  <div className="text-sm">
                    <div className="font-semibold">{rawImage ? "New logo ready · 1200px" : "Current logo"}</div>
                    <div className="text-xs text-[var(--fg-muted)]">Click to replace · auto-fit (no crop)</div>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-6 h-6 mx-auto text-[var(--fg-muted)]" />
                  <div className="mt-2 text-sm">Drop image or click to upload</div>
                  <div className="text-xs text-[var(--fg-muted)]">PNG / JPG · ≤ 800 KB</div>
                </>
              )}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => readFile(e.target.files?.[0])} />
            </div>
            {rawImage && (
              <div className="mt-2 flex items-center gap-3 text-xs text-[var(--fg-muted)]">
                <span className="uppercase tracking-widest">Background</span>
                <input data-testid="edit-bg-color-input" type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} className="h-7 w-10 rounded border border-[var(--line-strong)] bg-transparent p-0 cursor-pointer" aria-label="Logo background color" />
                <div className="flex gap-1">
                  {["#ffffff", "#000000", "#0a0a10", "#8b5cf6", "#06b6d4", "#f59e0b"].map((c) => (
                    <button key={c} type="button" onClick={() => setBgColor(c)} className="h-6 w-6 rounded-full border border-white/10 hover:scale-110 transition" style={{ background: c }} aria-label={`Set background ${c}`} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {error && <div className="text-sm text-red-400" data-testid="edit-error">{error}</div>}

          <button data-testid="save-edit-block" onClick={submit} disabled={busy} className="w-full btn-primary disabled:opacity-50">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </aside>
    </>
  );
}
